import { useCallback, useEffect, useState } from "react";
import { Markdown } from "../components/Markdown";
import {
  api,
  type FeatureRequestRow,
  type FeedbackStatus,
} from "../lib/api";

const STATUS_LABEL: Record<FeedbackStatus, string> = {
  new: "New",
  acknowledged: "Acknowledged",
  todo: "To do",
  done: "Done",
  archived: "Archived",
};

const STATUS_CHIP: Record<FeedbackStatus, string> = {
  new: "bg-slate-100 text-slate-700",
  acknowledged: "bg-blue-100 text-blue-800",
  todo: "bg-amber-100 text-amber-800",
  done: "bg-emerald-100 text-emerald-800",
  archived: "bg-slate-100 text-slate-500",
};

// Mirror of backend ``NON_VOTEABLE_STATUSES``: terminal states close voting.
// ``archived`` rows aren't returned by the public list at all; ``done`` rows
// ARE returned (so DataCampers see what shipped) but voting on them is a 404.
const NON_VOTEABLE_STATUSES: ReadonlySet<FeedbackStatus> = new Set([
  "done",
  "archived",
] as const);

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// Mirror of backend ranking in ``list_active_feature_requests``: votes desc,
// then newest first. Applied after a local vote toggle so the in-memory list
// stays consistent with what a refresh would show.
function rankFeatureRequests(rows: FeatureRequestRow[]): FeatureRequestRow[] {
  return [...rows].sort((a, b) => {
    if (b.vote_count !== a.vote_count) return b.vote_count - a.vote_count;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
}

/** Public ranked list of feature requests with a vote toggle per row. */
export function FeatureRequestsPage() {
  const [rows, setRows] = useState<FeatureRequestRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    api.feedback
      .listFeatureRequests()
      .then((r) => setRows(r.items))
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(load, [load]);

  async function toggleVote(id: string) {
    setBusyId(id);
    setError(null);
    try {
      const result = await api.feedback.toggleVote(id);
      setRows((cur) => {
        if (!cur) return cur;
        const patched = cur.map((r) =>
          r.id === id
            ? { ...r, vote_count: result.vote_count, has_voted: result.has_voted }
            : r,
        );
        // Re-sort so the local view matches the backend's ranking after a
        // vote that changes relative position; otherwise the row would stay
        // visually stale until a manual reload.
        return rankFeatureRequests(patched);
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  if (rows === null && !error) {
    return <p className="text-slate-500">Loading…</p>;
  }

  return (
    <div>
      <h1 className="mb-2 text-2xl font-semibold">Feature requests</h1>
      <p className="mb-4 text-sm text-slate-500">
        Ideas anyone at @datacamp.com has submitted. Click a row to upvote — the
        admin team uses the rankings to triage what to build next. To file a new
        one, click the <strong>Feedback</strong> button bottom-right.
      </p>

      {error && (
        <p className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}

      {rows && rows.length === 0 && (
        <p className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">
          No feature requests yet. Be the first — open the Feedback dialog.
        </p>
      )}

      <ul className="space-y-3">
        {rows?.map((r) => (
          <li
            key={r.id}
            className="rounded-lg border border-slate-200 bg-white p-4"
          >
            <div className="flex items-start gap-4">
              {(() => {
                const locked = NON_VOTEABLE_STATUSES.has(r.status);
                const disabled = locked || busyId === r.id;
                const lockTitle =
                  r.status === "done"
                    ? "Voting closed — this request has shipped"
                    : "Voting closed — this request was archived";
                return (
                  <button
                    type="button"
                    onClick={() => toggleVote(r.id)}
                    disabled={disabled}
                    title={locked ? lockTitle : undefined}
                    aria-disabled={locked}
                    className={`flex w-16 flex-col items-center gap-0.5 rounded-md border px-2 py-2 text-sm font-medium ${
                      locked
                        ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
                        : r.has_voted
                          ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                          : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                    } disabled:opacity-50`}
                    aria-pressed={r.has_voted}
                  >
                    <span aria-hidden>▲</span>
                    <span className="font-mono text-base">{r.vote_count}</span>
                  </button>
                );
              })()}
              <div className="flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <h2 className="text-base font-semibold">{r.title}</h2>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CHIP[r.status]}`}
                  >
                    {STATUS_LABEL[r.status]}
                  </span>
                </div>
                <Markdown
                  source={r.body}
                  className="mt-1 text-slate-700"
                />
                <p className="mt-2 text-xs text-slate-500">
                  Filed by {r.author_display_name}
                  {r.author_kind === "mcp" && (
                    <span className="ml-1 inline-block rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700">
                      via MCP
                    </span>
                  )}{" "}
                  on {formatTime(r.created_at)}
                </p>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
